import { computed, toValue } from 'vue'
import type { MaybeRef, Ref } from 'vue'
import type {
  IdfJourneyTemplate,
  IdfJourneyTemplateGrondbank,
  JourneyValidationErrors,
  JourneyValidationKey,
} from '@/types/idf-journey-product'
import type { OrganizationStroomFieldConfig } from '@/types/organization-config'
import { useLegacyAppStore } from '@/store/legacy-app.store'
import { storeToRefs } from 'pinia'
import * as modules from '@/utils/modules'

export function useJourneyValidation(
  journeyTemplate: Ref<IdfJourneyTemplate>,
  journeyTemplateGrondbank: Ref<IdfJourneyTemplateGrondbank | null>,
  parameters: MaybeRef<OrganizationStroomFieldConfig[]>,
  grondbankToggle?: MaybeRef<boolean>
) {
  const appStore = useLegacyAppStore()
  const { subModuleIds } = storeToRefs(appStore)

  function shouldSkip(key: JourneyValidationKey): boolean {
    return toValue(skipFields)?.includes(key) ?? false
  }

  const skipFields = computed<JourneyValidationKey[]>(() => {
    const skip: JourneyValidationKey[] = []
    if (
      subModuleIds.value.includes(modules.PROJECTS_LITE_NO_PRODUCTS_SUBMODULE_ID) ||
      subModuleIds.value.includes(modules.ACTIVE_PROJECTS_NO_PRODUCTS_SUBMODULE_ID) ||
      subModuleIds.value.includes(modules.ACTIVE_PROJECTS_NO_PRODUCTS_GRONDBANK_SUBMODULE_ID)
    ) {
      skip.push('materialen')
    }

    if (subModuleIds.value.includes(modules.ACTIVE_PROJECTS_NO_VERVOERDER_GRONDBANK_SUBMODULE_ID)) {
      skip.push('stroomVervoerder')
    }

    if (subModuleIds.value.includes(modules.ACTIVE_PROJECTS_SCHIP_SUBMODULE_ID)) {
      skip.push('stroomProducent')
      skip.push('verzendAdres')
    }

    // if grondbank is active we no longer need to validate stroomname
    if (toValue(grondbankToggle)) {
      skip.push('stroomName')
    }

    return skip
  })

  const errors = computed<JourneyValidationErrors>(() => {
    const template = journeyTemplate.value
    const templateGrondbank = journeyTemplateGrondbank.value
    const params = toValue(parameters)
    const result: JourneyValidationErrors = {}

    if (toValue(grondbankToggle) && templateGrondbank !== null) {
      if (!shouldSkip('stroomNameGrondbank') && !templateGrondbank.stroomNameGrondbank?.trim())
        result.stroomNameGrondbank = 'Grondtransportmelding is verplicht'
    }

    if (!shouldSkip('stroomName') && !template.stroomName?.trim())
      result.stroomName = 'Kenmerkformulier is verplicht'

    if (!shouldSkip('stroomProducent') && !template.stroomProducent)
      result.stroomProducent = 'Afvalstoffenproducent is verplicht'

    if (!shouldSkip('verzendAdres') && !template.verzendAdres)
      result.verzendAdres = 'Adres van verzending is verplicht'

    if (
      !shouldSkip('stroomVervoerder') &&
      (!template.stroomVervoerder || template.stroomVervoerder.length < 1)
    )
      result.stroomVervoerder = 'Voeg minimaal één vervoerder toe'

    if (!shouldSkip('stroomVerwerker') && !template.stroomVerwerker)
      result.stroomVerwerker = 'Verwerker is verplicht'

    if (!shouldSkip('bestemmingAdres') && !template.bestemmingAdres)
      result.bestemmingAdres = 'Verwerkerslocatie is verplicht'

    if (!shouldSkip('materialen') && !template.materialen?.length)
      result.materialen = 'Voeg minimaal één afvalstof toe'

    const extraOptionsErrors: Record<string, string> = {}

    for (const param of params) {
      if (param.required !== true || param.deleted) continue
      if (shouldSkip(`extraOptions.${param.key}`)) continue

      const value = template.extraOptions?.[param.key]

      if (value == null || (typeof value === 'string' && !value.trim())) {
        extraOptionsErrors[param.key] = `${param.fieldLabel} is verplicht`
      }
    }

    if (Object.keys(extraOptionsErrors).length > 0) {
      result.extraOptions = extraOptionsErrors
    }

    return result
  })

  const isValid = computed(() => Object.keys(errors.value).length === 0)

  function validate(): boolean {
    return isValid.value
  }

  return { errors, isValid, validate }
}
